import { useEffect, useState } from 'react';
import { Home, Briefcase, Scissors, Star, TrendingUp, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Services = ({ onBookNow }) => {
  const [services, setServices] = useState([]);
  const [hoveredService, setHoveredService] = useState(null);
  const [serviceStats, setServiceStats] = useState({});

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      const servicesData = response.data;
      setServices(servicesData);
      
      // Simulate popularity data
      const stats = {};
      servicesData.forEach(service => {
        stats[service.id] = {
          bookings_today: Math.floor(Math.random() * 50) + 10,
          trending: Math.random() > 0.6
        };
      });
      setServiceStats(stats);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'home':
        return <Home className="w-6 h-6" />;
      case 'briefcase':
        return <Briefcase className="w-6 h-6" />;
      case 'star':
        return <Star className="w-6 h-6" />;
      default:
        return <Scissors className="w-6 h-6" />;
    }
  };

  const handleServiceClick = (service) => {
    toast.success(`Selected: ${service.name}`);
    onBookNow(service);
  };

  return (
    <section id="services" data-testid="services-section" className="section bg-[var(--bg-primary)] dark:bg-[#111111]">
      <div className="container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[var(--text-primary)] mb-4" data-testid="services-heading">
            Book the perfect cut, anywhere
          </h2>
          <p className="text-base text-[var(--text-secondary)] leading-relaxed">
            Professional barber services delivered to your doorstep. Choose from our range of grooming services.
          </p>
        </div>

        {/* Services Grid - Bento Style with Animations */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
          {services.map((service, index) => (
            <div
              key={service.id}
              data-testid={`service-card-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`group relative bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border border-[var(--border-color)] rounded-lg p-6 cursor-pointer overflow-hidden
                transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl
                ${hoveredService === service.id ? 'border-[#00C167] shadow-lg shadow-[#00C167]/20' : ''}
                ${index === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
              onClick={() => handleServiceClick(service)}
              onMouseEnter={() => setHoveredService(service.id)}
              onMouseLeave={() => setHoveredService(null)}
              style={{
                animationDelay: `${index * 0.1}s`
              }}
            >
              {/* Trending Badge */}
              {serviceStats[service.id]?.trending && (
                <div className="absolute top-4 right-4 flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/20 border border-orange-500/40">
                  <TrendingUp className="w-3 h-3 text-orange-400" />
                  <span className="text-xs font-bold text-orange-400">Trending</span>
                </div>
              )}

              {/* Icon with animated background */}
              <div className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#00C167]/10 text-[#00C167] mb-4 overflow-hidden">
                <div className={`absolute inset-0 bg-[#00C167]/20 transform transition-transform duration-500 ${
                  hoveredService === service.id ? 'scale-150' : 'scale-0'
                }`}></div>
                <div className="relative z-10">
                  {getIcon(service.icon)}
                </div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2 group-hover:text-[#00C167] transition-colors">
                {service.name}
              </h3>
              <p className="text-[var(--text-secondary)] mb-4 text-sm">
                {service.description}
              </p>

              {/* Stats Row */}
              <div className="flex items-center justify-between text-sm mb-3">
                <div className="flex items-center gap-1 text-[var(--text-secondary)]">
                  <Clock className="w-4 h-4" />
                  <span>{service.duration} mins</span>
                </div>
                {serviceStats[service.id] && (
                  <div className="text-xs text-[var(--text-secondary)]">
                    {serviceStats[service.id].bookings_today} bookings today
                  </div>
                )}
              </div>

              {/* Price with animated underline */}
              <div className="relative">
                <div className="font-bold text-[#00C167] text-2xl">
                  ₦{service.price.toLocaleString()}
                </div>
                <div className={`absolute bottom-0 left-0 h-0.5 bg-[#00C167] transition-all duration-300 ${
                  hoveredService === service.id ? 'w-full' : 'w-0'
                }`}></div>
              </div>

              {/* Hover Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br from-[#00C167]/0 to-[#00C167]/5 pointer-events-none transition-opacity duration-300 ${
                hoveredService === service.id ? 'opacity-100' : 'opacity-0'
              }`}></div>

              {/* Animated border */}
              <div className={`absolute inset-0 rounded-lg border-2 border-[#00C167] pointer-events-none transition-opacity duration-300 ${
                hoveredService === service.id ? 'opacity-100' : 'opacity-0'
              }`}></div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#00C167]/10 border border-[#00C167]/30">
            <div className="w-2 h-2 rounded-full bg-[#00C167] animate-pulse"></div>
            <span className="text-[var(--text-primary)] font-medium">All services include professional tools and products</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        [data-testid^="service-card-"] {
          animation: slideUp 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </section>
  );
};

export default Services;